import type { Product, Variant } from "schemas/generated/product";

import { skipToken } from "@tanstack/react-query";
import { productRouter } from "@v2/trpc/routes/product";
import { useLocation, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

const createDefaultVariantValues = (): Variant => ({
  id: uuidv4(),
  title: "",
  price: null,
  originalPrice: null,
  priceCurrencyCode: "USD",
  unitCostCurrencyCode: "USD",
  optionValues: [],
  referenceType: "REPLO",
});

const createDefaultProductValues = (productType?: string): Product => ({
  id: uuidv4(),
  title: "",
  description: "",
  status: "draft",
  referenceType: "REPLO",
  type: productType ?? "UNASSIGNED",
  options: [],
  variants: [createDefaultVariantValues()],
});

interface ProductDetailsResponse {
  creatingNewProduct: boolean;
  product: Product;
  isLoading?: boolean;
}

export const useProductDetails = (): ProductDetailsResponse => {
  const { productId } = useParams<{ productId: string }>();
  const location = useLocation();

  const creatingNewProduct = location.pathname.includes("/products/new");

  const selectedProductType = location.state?.selectedProductType;

  const { data: product, isLoading: productLoading } =
    productRouter.get.useQuery(
      creatingNewProduct || !productId
        ? skipToken
        : { type: "id", id: productId },
    );

  return {
    creatingNewProduct,
    product: product ?? createDefaultProductValues(selectedProductType),
    isLoading: creatingNewProduct ? undefined : productLoading,
  };
};
